import React from "react";
import SideBar from "../components/Layout/Sidebar";
import Header from "../components/Layout/Header";
import Footer from "../components/Layout/Footer";
import Loader from "../components/Common/loader";
import { useEffect, useState } from "react";
import { useAlert } from "context/alert";
import {
  fetchDashboardData,
  fetchPatientGraph,
  fetchStatsGraph,
} from "services/dashboard";
import { STALE_TIME } from "utils/constants";
import { useQuery } from "@tanstack/react-query";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import CustomBarChart from "components/Common/Graphs/CustomBarChart";
import {
  FaMoneyBillWave,
  FaUserInjured,
  FaClipboardList,
  FaCalendarCheck,
  FaRupeeSign,
} from "react-icons/fa";

const Dashboard = () => {
  const { showAlert } = useAlert();

  const [selectedFilter, setSelectedFilter] = useState("Today");

  const { data } = useQuery(["fetch-stats-graph"], () => fetchStatsGraph(), {
    staleTime: STALE_TIME,
    onSuccess: (res) => {},
      onError: () => showAlert("Failed to fetch stats!", "danger"),
  });

  const { data: patientData } = useQuery(
    ["fetch-patient-graph"],
    () => fetchPatientGraph(),
    {
      staleTime: STALE_TIME,
      onSuccess: (res) => {},
      onError: () => showAlert("Failed to fetch patient!", "danger"),
    },
  );

  const franchiseId = localStorage.getItem("franchise_id");
  const subFranchise = localStorage.getItem("sub_franchise");

  const {
    data: cardsData,
    isLoading,
    refetch,
  } = useQuery(
    ["fetch-cards-data", selectedFilter, franchiseId],
    () =>
      fetchDashboardData({
        period: selectedFilter?.toUpperCase(),
        franchise_id: franchiseId,
        // zone_id: zoneId,
      }),
    {
      staleTime: STALE_TIME,
      select: (res) => {
        const response = res?.data;
        if (response && response.length) {
          res.data = response.map((x, index) => ({
            ...x,
            _index: index + 1,
          }));
        }
        return res;
      },
      onError: () => showAlert("Failed to fetch dashboard data!", "danger"),
      // enabled: !!rowData?.id,
    },
  );

  const [transformedData, setTransformedData] = useState({
    currentYearData: [],
    previousYearData: [],
    patientDataMapped: [],
  });

  useEffect(() => {
    if (data && patientData) {
      const patientDataMapped = Object.keys(
        patientData?.data?.patient_data,
      ).map((key) => ({
        month: key,
        patients: patientData?.data?.patient_data[key],
        appointments: patientData?.data?.appointment_data[key],
      }));

      const transformStatsData = (stats) => {
        const months = [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ];

        return months.map((month, index) => {
          return {
            name: month.substring(0, 3),
            patientCount: parseFloat(
              stats?.monthly_patient_count[index][month],
            ),
            appointmentCount: parseFloat(
              stats?.monthly_appointment_count[index][month],
            ),
            earnings: parseFloat(stats?.monthly_earning[index][month]),
          };
        });
      };
      const currentYearData = transformStatsData(
        data?.data?.current_year_stats,
      );
      const previousYearData = transformStatsData(
        data?.data?.previous_year_stats,
      );
      setTransformedData({
        currentYearData,
        previousYearData,
        patientDataMapped,
      });
    }
  }, [data, patientData]);

  const { currentYearData, previousYearData, patientDataMapped } =
    transformedData;

  const filters = [
    "Today",
    "Yesterday",
    "Weekly",
    "Monthly",
    "Yearly",
    "PrevWeekly",
    "PrevMonthly",
    "PrevYearly",
  ];

  const handleSelect = (filter) => {
    setSelectedFilter(filter);
  };

  const isSuperFranchiseCommon = Number(
    localStorage.getItem("is_super_franchise"),
  );
  const isFranchiseEmployeeCommon = Number(
    localStorage.getItem("is_franchise_employee"),
  );
  const isSubFranchiseCommon = Number(localStorage.getItem("sub_franchise"));

  const showBalances =
    isSuperFranchiseCommon === 0 &&
    isFranchiseEmployeeCommon === 0 &&
    isSubFranchiseCommon === 0;

  return (
    <>
      <div className="container-fluid mb-3">
        <div className="d-flex justify-content-end mb-4 align-items-center gap-2">
          {!showBalances && (
            <>
              <div className="d-flex align-items-center gap-2">
                <label
                  htmlFor="openingBalance"
                  className="form-check-label fw-bold"
                >
                  Opening Balance: {cardsData?.data?.opening_balance || 0}
                </label>
              </div>

              <div className="d-flex align-items-center gap-2">
                <label
                  htmlFor="closingBalance"
                  className="form-check-label fw-bold"
                >
                  Closing Balance: {cardsData?.data?.closing_amount || 0}
                </label>
              </div>
            </>
          )}
          <label htmlFor="dashboardDropdown" className="fw-semibold me-2">
            Select Filter:
          </label>
          <div className="dropdown">
            <button
              className="btn btn-primary dropdown-toggle px-4 py-2 rounded-2 shadow-sm"
              type="button"
              id="dashboardDropdown"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {selectedFilter}
            </button>
            <ul
              className="dropdown-menu dropdown-menu-end shadow"
              aria-labelledby="dashboardDropdown"
            >
              {filters.map((filter) => (
                <li key={filter}>
                  <button
                    className={`dropdown-item${
                      selectedFilter === filter ? " active" : ""
                    }`}
                    onClick={() => handleSelect(filter)}
                  >
                    {filter}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="row mb-4 highlightsBox">
          <div className="col-12 col-sm-6 col-md-4 col-lg col-xl">
            <div
              className="highlight"
              style={{
                background: "linear-gradient(135deg, #fef2f2 0%, #ffe4e6 100%)", // light pink
                padding: "0px",
                borderRadius: "16px",
                border: "1px solid #fecdd3",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  padding: "12px",
                  borderRadius: "12px",
                  background:
                    "linear-gradient(135deg, #f472b6 0%, #ec4899 100%)",
                  boxShadow: "0 10px 25px -5px rgba(244, 114, 182, 0.3)",
                }}
              >
                <FaUserInjured style={{ color: "white", fontSize: "24px" }} />
              </div>

              <div className="info">
                <div className="title">Total Patient</div>
                <div className="numbers">
                  {cardsData?.data?.pateint_count || 0}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-sm-6 col-md-4 col-lg col-xl">
            <div
              className="highlight"
              style={{
                background: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)", // light orange
                padding: "0px",
                borderRadius: "16px",
                border: "1px solid #fed7aa",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  padding: "12px",
                  borderRadius: "12px",
                  background:
                    "linear-gradient(135deg, #fb923c 0%, #f97316 100%)",
                  boxShadow: "0 10px 25px -5px rgba(251, 146, 60, 0.3)",
                }}
              >
                <FaClipboardList style={{ color: "white", fontSize: "24px" }} />
              </div>

              <div className="info">
                <div className="title">Pending Reports</div>
                <div className="numbers">
                  {cardsData?.data?.pending_report || 0}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-sm-6 col-md-4 col-lg col-xl">
            <div
              className="highlight"
              style={{
                background: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)", // light purple
                padding: "0px",
                borderRadius: "16px",
                border: "1px solid #ddd6fe",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  padding: "12px",
                  borderRadius: "12px",
                  background:
                    "linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)",
                  boxShadow: "0 10px 25px -5px rgba(167, 139, 250, 0.3)",
                }}
              >
                <FaCalendarCheck style={{ color: "white", fontSize: "24px" }} />
              </div>

              <div className="info">
                <div className="title">Booking Amount</div>
                <div className="numbers">
                  {cardsData?.data?.booking_amount || 0}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-sm-6 col-md-4 col-lg col-xl">
            <div
              className="highlight"
              style={{
                background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)", // light blue
                padding: "0px",
                borderRadius: "16px",
                border: "1px solid #bfdbfe",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  padding: "12px",
                  borderRadius: "12px",
                  background:
                    "linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)",
                  boxShadow: "0 10px 25px -5px rgba(96, 165, 250, 0.3)",
                }}
              >
                <FaRupeeSign style={{ color: "white", fontSize: "24px" }} />
              </div>

              <div className="info">
                <div className="title">Total Paid Amount</div>
                <div className="numbers">
                  {cardsData?.data?.total_paid_amount || 0}
                </div>
              </div>
            </div>
          </div>

          {subFranchise !== "1" && (
            <div className="col-12 col-sm-6 col-md-4 col-lg col-xl">
              <div
                className="highlight"
                style={{
                  background:
                    "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
                  padding: "0px",
                  borderRadius: "16px",
                  border: "1px solid #a7f3d0",
                }}
              >
                <div
                  style={{
                    display: "inline-flex",
                    padding: "12px",
                    borderRadius: "12px",
                    background:
                      "linear-gradient(135deg, #34d399 0%, #14b8a6 100%)",
                    boxShadow: "0 10px 25px -5px rgba(52, 211, 153, 0.3)",
                  }}
                >
                  <FaMoneyBillWave
                    style={{ color: "white", fontSize: "24px" }}
                  />
                </div>

                <div className="info">
                  <div className="title">Commission Amount</div>
                  <div className="numbers">
                    {cardsData?.data?.commission_total || 0}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div
          className="box mb-4 shadow-lg p-4 rounded"
          style={{ backgroundColor: "#ffffff" }}
        >
          <div className="boxHeader d-flex justify-content-between align-items-center mb-3">
            <h1 style={{ fontWeight: "bold", color: "#333" }}>Title</h1>
            <div className="right">
              <a
                href="#"
                className="btn btn-icon btn-radius-8px btn-primary d-flex align-items-center"
              >
                <img
                  src="../assets/img/add-circle-icon.png"
                  alt=""
                  width="16"
                  style={{ marginRight: "8px" }}
                />
                Button
              </a>
            </div>
          </div>

          <div className="boxContent">
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={patientDataMapped}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend verticalAlign="top" height={36} />
                <Line
                  type="monotone"
                  dataKey="patients"
                  stroke="#ff6347"
                  dot={{ r: 4 }}
                  strokeWidth={2}
                  activeDot={{ r: 6 }}
                  name="Patients"
                />
                <Line
                  type="monotone"
                  dataKey="appointments"
                  stroke="#1e90ff"
                  dot={{ r: 4 }}
                  strokeWidth={2}
                  activeDot={{ r: 6 }}
                  name="Appointments"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 mb-4">
            <div
              className="box shadow-lg p-4 rounded"
              style={{ backgroundColor: "#ffffff" }}
            >
              <div className="boxHeader mb-3">
                <h2 style={{ fontWeight: "bold", color: "#333" }}>
                  Current Year Stats
                </h2>
              </div>
              <div className="boxContent" style={{ height: "350px" }}>
                <CustomBarChart
                  data={currentYearData}
                  chartType="currentYear"
                />
              </div>
            </div>
          </div>
          <div className="col-md-6 mb-4">
            <div
              className="box shadow-lg p-4 rounded"
              style={{ backgroundColor: "#ffffff" }}
            >
              <div className="boxHeader mb-3">
                <h2 style={{ fontWeight: "bold", color: "#333" }}>
                  Previous Year Stats
                </h2>
              </div>
              <div className="boxContent" style={{ height: "350px" }}>
                <CustomBarChart
                  data={previousYearData}
                  chartType="previousYear"
                />
              </div>
            </div>
          </div>
        </div>

        {/* <div className="boxContent noPadding">
          <div className="contentScroll">
            <table className="table mb-0">
              <thead>
                <tr>
                  <th scope="col">Employee Name</th>
                  <th scope="col">Employee ID</th>
                  <th scope="col">Date OF JOINING</th>
                  <th scope="col">Job title</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div className="employee d-flex align-items-center">
                      <img
                        src="../assets/img/user-default.jpg"
                        width="32"
                        alt=""
                        title="User"
                      />
                      <span className="ms-2">Yashpal Singh</span>
                    </div>
                  </td>
                  <td>200192</td>
                  <td>17th Oct, 2019</td>
                  <td>
                    <span className="badge round16px success">Active</span>
                  </td>
                  <td>
                    <button
                      className="btn btn-white btn-sm box-shadow-none dropdown-toggle"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <i className="fa fa-ellipsis-v"></i>
                    </button>
                    <ul className="dropdown-menu">
                      <li>
                        <a
                          className="dropdown-item"
                          href="#"
                          data-bs-toggle="modal"
                          data-bs-target="#editModal"
                        >
                          Edit
                        </a>
                      </li>
                      <li>
                        <a className="dropdown-item" href="#">
                          Another action
                        </a>
                      </li>
                      <li>
                        <a className="dropdown-item" href="#">
                          Something else here
                        </a>
                      </li>
                    </ul>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            className="modal fade"
            id="editModal"
            tabIndex="-1"
            aria-labelledby="editModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h1 className="modal-title fs-5" id="editModalLabel">
                    Modal title
                  </h1>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <form method="POST">
                    <div className="form-group mb-3">
                      <label>Label</label>
                      <input
                        type="text"
                        placeholder="Some text"
                        className="form-control is-invalid"
                        aria-label="Some text"
                        aria-describedby="inputGroup-sometext"
                      />
                      <div className="invalid-feedback">Error message</div>
                    </div>
                    <div className="form-group mb-3">
                      <label>Label</label>
                      <input
                        type="text"
                        placeholder="Some text"
                        className="form-control"
                        aria-label="Some text"
                        aria-describedby="inputGroup-sometext"
                      />
                    </div>
                    <div className="form-group mb-0">
                      <label>Label</label>
                      <input
                        type="text"
                        placeholder="Some text"
                        className="form-control"
                        aria-label="Some text"
                        aria-describedby="inputGroup-sometext"
                      />
                    </div>
                  </form>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-primary"
                    data-bs-dismiss="modal"
                  >
                    Button
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div> */}
        {/* <div className="row mb-4">
                        <div className="col-md-6">
                            <div className="box">
                                <div className="boxHeader">
                                    <h1>With labels</h1>
                                </div>

                                <div className="boxContent">
                                    <form method="POST">
                                        <div className="form-group mb-3">
                                            <label>Label</label>
                                            <input type="text" placeholder="Some text" className="form-control is-invalid" aria-label="Some text" aria-describedby="inputGroup-sometext" />
                                            <div className="invalid-feedback">
                                                Error message
                                            </div>
                                        </div>
                                        <div className="form-group mb-3">
                                            <label>Label</label>
                                            <input type="text" placeholder="Some text" className="form-control" aria-label="Some text" aria-describedby="inputGroup-sometext" />
                                        </div>
                                        <div className="form-group mb-3">
                                            <label>Label</label>
                                            <input type="text" placeholder="Some text" className="form-control" aria-label="Some text" aria-describedby="inputGroup-sometext" />
                                        </div>
                                        <button className="btn btn-primary">
                                            Button
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="box">
                                <div className="boxHeader">
                                    <h1>Without labels</h1>
                                </div>

                                <div className="boxContent">
                                    <form method="POST">
                                        <div className="form-group mb-3">
                                            <input type="text" placeholder="Some text" className="form-control" aria-label="Some text" aria-describedby="inputGroup-sometext" />
                                        </div>
                                        <div className="form-group mb-3">
                                            <input type="text" placeholder="Some text" className="form-control" aria-label="Some text" aria-describedby="inputGroup-sometext" />
                                        </div>
                                        <div className="form-group mb-3">
                                            <input type="text" placeholder="Some text" className="form-control" aria-label="Some text" aria-describedby="inputGroup-sometext" />
                                        </div>
                                        <div className="form-group mb-3">
                                            <textarea placeholder="Some text" className="form-control" aria-label="Some text" aria-describedby="inputGroup-sometext" />
                                        </div>
                                        <button className="btn btn-primary">
                                            Button
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div> */}

        {/* <div className="box">
                        <div className="boxHeader">
                            <h1>Inline form</h1>
                        </div>

                        <div className="boxContent">
                            <form method="POST">
                                <div className="row">
                                    <div className="col-lg-3 col-md-12">
                                        <div className="form-group mb-3">
                                            <input type="text" placeholder="Some text" className="form-control" aria-label="Some text" aria-describedby="inputGroup-sometext" />
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-md-12">
                                        <div className="form-group mb-3">
                                            <input type="text" placeholder="Some text" className="form-control" aria-label="Some text" aria-describedby="inputGroup-sometext" />
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-md-12">
                                        <div className="form-group mb-3">
                                            <input type="text" placeholder="Some text" className="form-control" aria-label="Some text" aria-describedby="inputGroup-sometext" />
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-md-12">
                                        <button className="btn btn-fullWidth btn-primary">
                                            Button
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div> */}
      </div>
      <Footer />
      <Loader />
    </>
  );
};

export default Dashboard;
